import { z } from 'zod'

/**
 * Hard byte cap for a mini-app `manifest.json`. The manifest is small metadata
 * (name/version/permissions/icon), never bulk data; the cap bounds parse work
 * and read memory, and defends every manifest reader against a tampered/huge
 * file. Enforced by the server resolver (apps/api/.../manifest-resolve.ts) AND
 * the icon read-cache sync (features/files/core/miniapp-icon.ts).
 */
export const MAX_MANIFEST_BYTES = 64 * 1024

const allowlistField = z.union([z.array(z.string()), z.literal('*')]).optional()

// Identity field names (email, name, role, org, teams, team_role, …) must be
// identifier-shaped — see the `wants_identity` comment for why the charset is a
// security boundary, not just tidiness.
const identityField = z.string().min(1).max(64).regex(/^[a-z0-9_]+$/i)

export const AppHookEventSchema = z.enum([
  'file:created',
  'file:updated',
  'file:moved',
  'file:deleted',
  'file:restored',
])

export const AppHookSchema = z.object({
  on: AppHookEventSchema,
  // R14 #105 — pattern length cap at the input boundary. Defense-in-depth
  // even though the matcher (minimatch) caps internally at 64 KB. No
  // legitimate hook pattern needs 1024 chars; reject early at upload time.
  match: z.string().max(1024).optional(),
  run: z.string().min(1),
})

export const MiniappManifestSchema = z.object({
  name: z.string().min(1).max(128),
  version: z.string().min(1).max(64),
  description: z.string().max(2048).optional(),
  entry_html: z.string().optional(),
  allowed_tools: allowlistField,
  allowed_models: allowlistField,
  allowed_skills: allowlistField,
  who_pays: z.enum(['owner', 'user']).optional(),
  app_hooks: z.array(AppHookSchema).max(64).optional(),
  // NOT an enum: a typo'd or newer-than-this-server key is IGNORED at CONSUMPTION
  // (graceful + version-tolerant) — buildEnvelope filters `consented` to CONSENT_KEYS
  // and each PII field populates only inside a `granted.has` guard, so a non-canonical
  // entry can never DISCLOSE anything. (R2-7c)
  //
  // But these names are also RENDERED in the consent dialog, so disclosure-safety isn't
  // enough: an HTML-special name (`x" onfocus=…`) is an injection vector if a renderer
  // ever forgets to escape — which embed.html's `fieldRow` did (R5). The `identifier`
  // charset cap makes that structurally impossible at the boundary while PRESERVING the
  // version-tolerance above (any identifier-shaped key — canonical, future, or typo'd —
  // still parses and is ignored at consumption). The renderers escape regardless.
  // R27 — element-count cap for parity with app_hooks (.max(64)). Real identity
  // sets are < 10 fields; without a cap, the 64 KiB manifest budget alone permits
  // ~16k entries that fan out into the consent message `.join` + one rendered DOM
  // row each. 32 is generous headroom; bounds the consent-UI/error work.
  wants_identity: z.array(identityField).max(32).optional(),
  wants_identity_optional: z.array(identityField).max(32).optional(),
  // #207 — client→client WS emit capability gate (the developer's fail-closed self-limit;
  // the only server-side containment for untrusted Mode A client JS if the page is XSS'd).
  // Resolved authoritatively from the app folder at ws-token mint, baked into the token, and
  // enforced at the inbound `event` frame — NOT forgeable by client JS. Absent ⇒ no client
  // emit (today's behavior — only server scripts emit). Each surface opts in explicitly.
  /** Gates `papan.events.emit()` (actor / self scope). */
  allowed_ws_self_emission: z.boolean().optional(),
  /** Gates `papan.events.broadcast()` (whole-app scope). */
  allowed_ws_broadcast: z.boolean().optional(),
  /** Which filter DIMENSIONS `papan.events.emitTo(filter, …)` may target. `[]`/absent ⇒ no
   *  targeted emit. `email` + `tenant` ship first (#207); `role`/`team` are deferred pending
   *  the cross-org role-context question, so they're not accepted here yet. */
  allowed_ws_targeted_emissions: z.array(z.enum(['email', 'tenant'])).max(2).optional(),
  /** Papan chrome bar config. `hide` drops items from the always-present bar
   *  (e.g. show only `account`, or only `permissions`). The bar itself is
   *  platform-owned and consistent across the previewer + ppn.gg. */
  bar: z.object({
    hide: z.array(z.enum(['brand', 'account', 'permissions'])).optional(),
  }).strict().optional(),
  // Custom launcher icon (a MiniappIconSpec). This is the PORTABLE source of
  // truth — it travels with the app in a download/export zip, so a
  // download → edit → re-upload round-trip preserves the icon (the Postgres
  // `files.metadata_json` copy is a derived read-cache resynced from here; see
  // features/files/core/miniapp-icon.ts). Kept PERMISSIVE (`unknown`) on purpose:
  // the icon is cosmetic, so a hand-edited/malformed value must NEVER fail the
  // whole manifest parse and disable the app's elevation/consent. It's validated
  // separately with `MiniappIconSpecSchema` at read time via
  // `iconSpecFromManifest` (fail-closed to the name-derived default tile).
  icon: z.unknown().optional(),
  // Forward-compat "must-understand" marker (JWT `crit` pattern; workstream A /
  // F-critical). Lists the names of TOP-LEVEL fields a reader MUST understand to
  // run the app safely. A reader that doesn't know a field named here refuses to
  // run (fail-closed) rather than silently ignoring a security-affecting field.
  // Top-level ONLY (nested fields are never safety-load-bearing — a new safety
  // mode ships as a new top-level `critical`-marked field). See the portable
  // reader below; unknown-and-NOT-critical fields are tolerated (forward compat).
  critical: z.array(z.string().min(1).max(64)).max(32).optional(),
}).strict()

/**
 * The TOP-LEVEL manifest field names THIS reader build understands. Kept as an
 * EXPLICIT list (not reflected from Zod internals) so the `critical` gate has a
 * stable, reviewable contract: a manifest whose `critical` array names a field
 * absent from this set is refused by the portable reader. When a new top-level
 * field is added to the schema above, add its name here in the same change.
 */
export const KNOWN_MANIFEST_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'version',
  'description',
  'entry_html',
  'allowed_tools',
  'allowed_models',
  'allowed_skills',
  'who_pays',
  'app_hooks',
  'wants_identity',
  'wants_identity_optional',
  'allowed_ws_self_emission',
  'allowed_ws_broadcast',
  'allowed_ws_targeted_emissions',
  'bar',
  'icon',
  'critical',
])

/**
 * PORTABLE (forward-tolerant) manifest schema — used by every RUNTIME consumer
 * (serve, scripts, db, hooks, consent, ws-token) and by `manifest_get` /
 * folder-import. Unlike the strict authoring schema it `.passthrough()`es so
 * unknown TOP-LEVEL fields survive a read-modify-write round-trip instead of
 * being stripped, and app-hook objects likewise preserve unknown keys. Known
 * fields keep their strict validation. The `critical` gate (below) is what
 * turns "unknown" from silently-ignored into fail-closed when the author
 * demanded it.
 */
const AppHookPortableSchema = AppHookSchema.passthrough()
// The portable reader must preserve unknown keys at EVERY nested level, not just
// the root + app_hooks — else a forward manifest that adds e.g. `bar.future_opt`
// is rejected as invalid_manifest, breaking the forward-compat contract. Keep the
// known `hide` enum validated; `.passthrough()` only tolerates unknown KEYS (a
// new safety-affecting MODE still ships as a new top-level `critical` field).
const BarPortableSchema = z.object({
  hide: z.array(z.enum(['brand', 'account', 'permissions'])).optional(),
}).passthrough()
export const MiniappManifestPortableSchema = MiniappManifestSchema
  .extend({
    app_hooks: z.array(AppHookPortableSchema).max(64).optional(),
    bar: BarPortableSchema.optional(),
  })
  .passthrough()

export type AppHookEvent = z.infer<typeof AppHookEventSchema>
export type AppHook = z.infer<typeof AppHookSchema>
export type MiniappManifest = z.infer<typeof MiniappManifestSchema>

export interface ManifestParseSuccess {
  ok: true
  manifest: MiniappManifest
}
export interface ManifestParseFailure {
  ok: false
  error: string
  issues: Array<{ path: string; message: string }>
}
export type ManifestParseResult = ManifestParseSuccess | ManifestParseFailure

export function parseManifest(input: unknown): ManifestParseResult {
  const result = MiniappManifestSchema.safeParse(input)
  if (result.success) return { ok: true, manifest: result.data }
  return {
    ok: false,
    error: 'invalid_manifest',
    issues: result.error.issues.map((i) => ({
      path: i.path.map(String).join('.'),
      message: i.message,
    })),
  }
}

export function parseManifestJSON(jsonText: string): ManifestParseResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonText)
  } catch (e) {
    return { ok: false, error: 'invalid_json', issues: [{ path: '', message: String((e as Error).message) }] }
  }
  return parseManifest(parsed)
}

// ── Portable (forward-tolerant) reader — workstream A ────────────────────────

/**
 * Reasons a portable read can fail. `unsupported_critical` is DISTINCT from an
 * ordinary parse error: it means the manifest is well-formed but demands a
 * capability this runtime does not understand (`critical` names an unknown
 * field) — the app must be REFUSED, not run degraded. `manifest_too_large`
 * bounds allocation before the JSON is even parsed.
 */
export type PortableManifestError =
  | 'invalid_json'
  | 'invalid_manifest'
  | 'manifest_too_large'
  | 'unsupported_critical'

export interface PortableManifestSuccess {
  ok: true
  manifest: MiniappManifest
  /** Unknown top-level field names that were tolerated (preserved, not understood). */
  unknownFields: string[]
}
export interface PortableManifestFailure {
  ok: false
  error: PortableManifestError
  issues: Array<{ path: string; message: string }>
  /** For `unsupported_critical`: the critical field name(s) this reader can't honor. */
  unsupportedCritical?: string[]
}
export type PortableManifestResult = PortableManifestSuccess | PortableManifestFailure

/**
 * Forward-tolerant manifest read. Unknown top-level fields are PRESERVED (never
 * reject the app), UNLESS the author listed one in `critical` — then the app is
 * refused (fail-closed must-understand). Known fields keep strict validation.
 * Use this for every runtime/serve/consent consumer; keep {@link parseManifest}
 * (strict) for authoring/scaffold/`manifest_set`.
 */
export function parsePortableManifest(input: unknown): PortableManifestResult {
  const result = MiniappManifestPortableSchema.safeParse(input)
  if (!result.success) {
    return {
      ok: false,
      error: 'invalid_manifest',
      issues: result.error.issues.map((i) => ({
        path: i.path.map(String).join('.'),
        message: i.message,
      })),
    }
  }
  const data = result.data as MiniappManifest & Record<string, unknown>
  const unknownFields = Object.keys(data).filter((k) => !KNOWN_MANIFEST_FIELDS.has(k))
  // `critical` gate — any must-understand field this reader doesn't know = refuse.
  const critical = Array.isArray(data.critical) ? data.critical : []
  const unsupportedCritical = critical.filter((name) => !KNOWN_MANIFEST_FIELDS.has(name))
  if (unsupportedCritical.length > 0) {
    return {
      ok: false,
      error: 'unsupported_critical',
      unsupportedCritical,
      issues: unsupportedCritical.map((name) => ({
        path: 'critical',
        message: `manifest marks unknown field "${name}" as critical; this runtime cannot run the app safely`,
      })),
    }
  }
  return { ok: true, manifest: result.data as MiniappManifest, unknownFields }
}

/**
 * Portable read from raw JSON text with the byte cap enforced BEFORE parsing, so
 * every reader (including WS-token / consent, which previously read the whole
 * body first) bounds allocation the same way.
 */
export function parsePortableManifestJSON(jsonText: string): PortableManifestResult {
  // Isomorphic: Buffer on the server, TextEncoder in a browser (no Buffer there).
  const bytes = typeof Buffer !== 'undefined' ? Buffer.byteLength(jsonText, 'utf8') : new TextEncoder().encode(jsonText).length
  if (bytes > MAX_MANIFEST_BYTES) {
    return {
      ok: false,
      error: 'manifest_too_large',
      issues: [{ path: '', message: `manifest exceeds ${MAX_MANIFEST_BYTES} bytes` }],
    }
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonText)
  } catch (e) {
    return { ok: false, error: 'invalid_json', issues: [{ path: '', message: String((e as Error).message) }] }
  }
  return parsePortableManifest(parsed)
}

// R14 #105 — matchesHook intentionally lives in `apps/api/src/v1/manifest-resolve.ts`
// (the dispatcher's neighbor), not here. The matcher uses `minimatch`, which is
// the same library the agent's glob tool uses and the post-CVE-patched version
// (10.2.5). Keeping `@papan/types` runtime-dependency-free means downstream
// type-only consumers don't pull in minimatch.
